import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  Stack,
  Switch
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { DataGrid } from '@mui/x-data-grid';
import { useIntl } from 'react-intl';
import Tooltip from '@mui/material/Tooltip';
import ClearIcon from '@mui/icons-material/Clear';
import Divider from '@mui/material/Divider';
import AssistantRoundedIcon from '@mui/icons-material/AssistantRounded';
import MDInput from 'components/MDInput';
import MDButton from 'components/MDButton';
import { useGetUsersQuery } from 'services/userApi';
import { useGetApiKeysQuery } from 'services/apiKeyApi';
import { useNotification } from 'hook/useNotification';
import IndeterminateLoading from 'components/Loading/IndeterminateLoading';
import MDTypography from 'components/MDTypography';
import ExpandableTextArea from 'components/ExpandableTextArea';
import CodeInput from 'components/inputs/CodeInput';
import InlineIconSelector from 'components/InlineIconSelector';
import ColorPicker from 'components/ColorPicker';

/**
 * Component for displaying and managing the details of an Assistant. Allows viewing,
 * creating, and updating Assistants.
 *
 * @component
 *
 * @example
 * <AssistantDetails assistant={assistant} onSubmit={handleSubmit} />
 *
 * @param {Object} props - The properties object.
 * @param {Object} props.assistant - The Assistant object to be displayed and edited.
 * @param {Function} props.onSubmit - The function to call when the form is submitted.
 * @param {Function} props.onFieldChange - The function to call when the fields value change.
 *
 * @returns {React.Component} The Assistant Details component.
 */
function AssistantDetails({ assistant, onSubmit, onFieldChange }) {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();

  const [apiKeySelectionModel, setApiKeySelectionModel] = useState([]);
  const [userSelectionModel, setUserSelectionModel] = useState([]);

  const {
    data: apiKeys = [],
    isLoading: apiKeyLoading,
    isError: apiKeyError
  } = useGetApiKeysQuery();
  const { data: users = [], isLoading: userLoading, isError: userError } = useGetUsersQuery();

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty }
  } = useForm({
    defaultValues: {
      id: '',
      label: '',
      code: '',
      description: '',
      welcoming: '',
      color: '#9c27b0',
      icon: 'account_circle',
      isPublic: false,
      apiKeys: [],
      users: []
    }
  });

  watch();

  useEffect(() => {
    if (onFieldChange) {
      onFieldChange(isDirty);
    }
  }, [isDirty, onFieldChange]);

  const resetAssistant = useCallback(() => {
    if (assistant?.code) {
      reset({
        ...assistant
      });
    } else {
      reset({
        id: '',
        label: '',
        code: '',
        description: '',
        welcoming: '',
        color: '#9c27b0',
        icon: 'account_circle',
        apiKeys: [],
        users: []
      });
    }
    setApiKeySelectionModel(assistant?.apiKeys.map((key) => key.id) ?? []);
    setUserSelectionModel(assistant?.users.map((user) => user.id) ?? []);
  }, [assistant, reset]);

  useEffect(() => {
    resetAssistant();
  }, [assistant, resetAssistant]);

  const handleReset = () => {
    resetAssistant();
  };

  const clearField = (fieldName) => {
    setValue(fieldName, '');
  };

  const handleApiKeySelectionChange = (selectionModel) => {
    const selectedApiKeys = selectionModel.map((id) => apiKeys.find((key) => key.id === id));
    reset((prevValues) => ({
      ...prevValues,
      apiKeys: selectedApiKeys
    }));
    setValue('apiKeys', selectedApiKeys);
  };

  const handleUserSelectionChange = (selectionModel) => {
    const selectedUsers = selectionModel.map((id) => users.find((user) => user.id === id));
    reset((prevValues) => ({
      ...prevValues,
      users: selectedUsers
    }));
    setValue('users', selectedUsers);
  };

  const handleApiKeyRowSelectionModelChange = (newRowSelectionModel) => {
    const selectedApiKeys = newRowSelectionModel.map((id) => apiKeys.find((key) => key.id === id));
    setApiKeySelectionModel(newRowSelectionModel);
    setValue('apiKeys', selectedApiKeys);
  };

  const handleUserRowSelectionModelChange = (newRowSelectionModel) => {
    const selectedUsers = newRowSelectionModel.map((id) => users.find((user) => user.id === id));
    setUserSelectionModel(newRowSelectionModel);
    setValue('users', selectedUsers);
  };

  const isCreating = !assistant?.id;

  if (userError || apiKeyError) {
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.assistant.fetch.error' })
      });
    } catch {
      // Ignore exception
    }
  }

  if (userLoading || apiKeyLoading) {
    return <IndeterminateLoading />;
  }

  return (
    <>
      <Stack
        component="form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        display="flex"
        direction="column"
        sx={{ height: '100%' }}
      >
        <Stack display="flex" direction="row">
          <AssistantRoundedIcon color="text" fontSize="medium" sx={{ mr: '0.75rem' }} />
          <MDTypography variant="h5">
            {msg({
              id: isCreating
                ? 'page.assistants.details.creation.title'
                : 'page.assistants.details.modification.title'
            })}
          </MDTypography>
        </Stack>
        <Divider sx={{ mt: '0.5rem', mb: '0.75rem' }} />
        <Stack
          spacing={1}
          display="flex"
          direction="column"
          flex={1}
          overflow="auto"
          mb={2}
          sx={{ scrollbarWidth: 'thin' }}
        >
          <Controller
            name="isPublic"
            control={control}
            render={({ field }) => (
              <Box id="isPublicContainer" sx={{ marginLeft: '10px !important', maxHeight: '40px' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={field.value}
                      onChange={(e) => field.onChange(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={msg({ id: 'field.isPublic.label' })}
                  sx={{ alignSelf: 'flex-start', mb: 1 }}
                />
              </Box>
            )}
          />
          {!isCreating && (
            <Controller
              name="id"
              control={control}
              render={({ field }) => (
                <MDInput
                  {...field}
                  label={msg({ id: 'field.id.label' })}
                  variant="filled"
                  fullWidth
                  required
                  size="small"
                  InputProps={{
                    readOnly: true
                  }}
                />
              )}
            />
          )}
          <Controller
            name="label"
            control={control}
            rules={{ required: msg({ id: 'field.label.required' }) }}
            render={({ field }) => (
              <MDInput
                required
                {...field}
                label={msg({ id: 'field.label.label' })}
                variant="filled"
                fullWidth
                size="small"
                error={!!errors.label}
                helperText={errors.label ? errors.label.message : ''}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={msg({ id: 'tooltip.clear' })}>
                        <IconButton onClick={() => clearField('label')}>
                          <ClearIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
            )}
          />
          <Controller
            name="code"
            control={control}
            rules={{ required: msg({ id: 'field.code.required' }) }}
            render={({ field }) => (
              <CodeInput field={field} errors={errors} clearField={() => clearField('code')} />
            )}
          />
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <MDInput
                {...field}
                label={msg({ id: 'field.description.label' })}
                variant="filled"
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={msg({ id: 'tooltip.clear' })}>
                        <IconButton onClick={() => clearField('description')}>
                          <ClearIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  )
                }}
              />
            )}
          />
          <Controller
            name="welcoming"
            control={control}
            render={({ field }) => (
              <ExpandableTextArea
                field={field}
                error={!!errors.content}
                helperText={errors.content ? errors.content.message : ''}
                clearField={() => clearField('welcoming')}
                label={msg({ id: 'field.welcoming.label' })}
                control={control}
                minRows={3}
              />
            )}
          />
          <Controller
            name="icon"
            control={control}
            render={({ field }) => (
              <InlineIconSelector
                value={field.value}
                onChange={(iconName) => field.onChange(iconName)}
              />
            )}
          />
          <Controller
            name="color"
            control={control}
            render={({ field }) => (
              <ColorPicker
                value={field.value}
                onChange={(color) => field.onChange(color)}
                label={msg({ id: 'field.color.label' })}
              />
            )}
          />

          <FormControl fullWidth sx={{ mt: '16px !important' }}>
            <FormLabel component="legend" id="api-keys-label" sx={{ fontSize: 'medium' }}>
              {msg({ id: 'field.apiKeys.label' })}
            </FormLabel>
            <Controller
              name="apiKeys"
              control={control}
              render={({ field }) => (
                <DataGrid
                  {...field}
                  rows={apiKeys}
                  columns={[
                    { field: 'id', headerName: 'ID', width: 50, resizable: false },
                    { field: 'key', headerName: 'Key', width: 200, resizable: false },
                    { field: 'owner', headerName: 'Owner', width: 150, resizable: false }
                  ]}
                  initialState={{
                    density: 'compact'
                  }}
                  checkboxSelection
                  rowHeight={38}
                  loading={apiKeyLoading}
                  onSelectionModelChange={handleApiKeySelectionChange}
                  rowSelectionModel={apiKeySelectionModel}
                  onRowSelectionModelChange={handleApiKeyRowSelectionModelChange}
                  disableColumnResize
                  hideFooter
                  sx={{ minHeight: apiKeys?.length < 1 ? '100px' : 'none' }}
                />
              )}
            />
          </FormControl>
          <FormControl fullWidth sx={{ mt: '16px !important' }}>
            <FormLabel component="legend" id="users-label" sx={{ fontSize: 'medium' }}>
              {msg({ id: 'field.users.label' })}
            </FormLabel>
            <Controller
              name="users"
              control={control}
              render={({ field }) => (
                <DataGrid
                  {...field}
                  rows={users}
                  columns={[
                    { field: 'id', headerName: 'ID', width: 50, resizable: false },
                    { field: 'login', headerName: 'Login', width: 150, resizable: false },
                    { field: 'firstname', headerName: 'Prénom', width: 150, resizable: false },
                    { field: 'lastname', headerName: 'Nom', width: 150, resizable: false }
                  ]}
                  initialState={{
                    density: 'compact'
                  }}
                  checkboxSelection
                  rowHeight={38}
                  loading={userLoading}
                  onSelectionModelChange={handleUserSelectionChange}
                  rowSelectionModel={userSelectionModel}
                  onRowSelectionModelChange={handleUserRowSelectionModelChange}
                  disableColumnResize
                  hideFooter
                />
              )}
            />
          </FormControl>
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '30px',
              background: `linear-gradient(to bottom, transparent, rgba(255, 255, 255, 0.9))`
            }}
          />
        </Stack>
        <Divider sx={{ mt: 0 }} />
        <Stack direction="row" spacing={2} sx={{ marginTop: 'auto !important' }}>
          <MDButton type="button" fullWidth color="secondary" onClick={handleReset}>
            {msg({ id: 'button.reset' })}
          </MDButton>
          <MDButton type="submit" fullWidth color="primary">
            {isCreating ? msg({ id: 'button.create' }) : msg({ id: 'button.update' })}
          </MDButton>
        </Stack>
      </Stack>
    </>
  );
}

AssistantDetails.propTypes = {
  assistant: PropTypes.shape({
    id: PropTypes.number,
    label: PropTypes.string,
    code: PropTypes.string,
    description: PropTypes.string,
    color: PropTypes.string,
    icon: PropTypes.string,
    welcoming: PropTypes.string,
    isPublic: PropTypes.bool,
    apiKeys: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        key: PropTypes.string,
        owner: PropTypes.string
      })
    ),
    users: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        login: PropTypes.string,
        firstname: PropTypes.string,
        lastname: PropTypes.string
      })
    )
  }),
  onSubmit: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired
};

export default AssistantDetails;
